import React from 'react';
import { ArrowLeft, User, Mail, Phone, Lock, AlertCircle } from 'lucide-react';

const CheckInForm = ({
    mode, setMode, formData, setFormData, loginIdentifier, setLoginIdentifier,
    loginPassword, setLoginPassword,
    onBack, onSubmit, onLogin, countries, status, tenant, t, formError
}) => {
    const [kvkkAccepted, setKvkkAccepted] = React.useState(false);

    const capitalizeWords = (str) => {
        if (!str) return '';
        return str.split(' ').map(word =>
            word.length > 0 ? word.charAt(0).toLocaleUpperCase('tr') + word.slice(1).toLocaleLowerCase('tr') : ''
        ).join(' ');
    };

    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.startsWith('0')) value = value.substring(1);
        if (value.length <= 10) {
            setFormData({ ...formData, phone: value });
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column' }}>
            {/* Header / Logo Section */}
            <div style={{ padding: '2rem', textAlign: 'center', position: 'relative' }}>
                <button 
                    onClick={onBack} 
                    style={{ position: 'absolute', left: '1.5rem', top: '2.5rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                >
                    <ArrowLeft />
                </button>
                {tenant?.theme?.logoUrl ? (
                    <img src={tenant.theme.logoUrl} alt="Logo" style={{ height: '50px', maxWidth: '150px', objectFit: 'contain' }} />
                ) : tenant?.name ? (
                    <div style={{ fontSize: '1.75rem', fontWeight: 900, letterSpacing: '2px', color: 'var(--primary)' }}>
                        {tenant.name}
                    </div>
                ) : (
                    <div style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', letterSpacing: '-0.02em', color: 'var(--primary)' }}>
                        ugra
                    </div>
                )}
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                <div className="card glass-premium" style={{ maxWidth: '450px', width: '100%', padding: '2.5rem', boxShadow: 'var(--shadow-lg)' }}>
                    
                    {/* Mode Switcher - Matched with CustomerLogin */}
                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', background: 'rgba(0,0,0,0.05)', padding: '4px', borderRadius: '12px' }}>
                        <button
                            onClick={() => { setMode('register'); }}
                            style={{
                                flex: 1,
                                padding: '10px',
                                borderRadius: '10px',
                                background: mode === 'register' ? 'white' : 'transparent',
                                color: mode === 'register' ? 'var(--text-main)' : 'var(--text-muted)',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                boxShadow: mode === 'register' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {t('checkin.modeRegister', 'Kayıt Ol')}
                        </button>
                        <button
                            onClick={() => { setMode('login'); }}
                            style={{
                                flex: 1,
                                padding: '10px',
                                borderRadius: '10px',
                                background: mode === 'login' ? 'white' : 'transparent',
                                color: mode === 'login' ? 'var(--text-main)' : 'var(--text-muted)',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                boxShadow: mode === 'login' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            {t('checkin.modeLogin', 'Giriş Yap')}
                        </button>
                    </div>

                    <h1 style={{ fontSize: '1.75rem', fontWeight: 800, textAlign: 'center', marginBottom: '0.5rem' }}>
                        {mode === 'register' 
                            ? (formData.firstName ? `Merhaba, ${formData.firstName}!` : t('checkin.title', 'Hoş Geldiniz')) 
                            : t('checkin.loginTitle', 'Tekrar Hoş Geldiniz')}
                    </h1>
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginBottom: '2.5rem' }}>
                        {mode === 'register' 
                            ? (formData.firstName ? 'Bilgilerini onaylayarak devam et.' : t('checkin.subtitle', 'Aramıza katılın, fırsatları kaçırmayın.')) 
                            : t('checkin.loginSubtitle', 'Portala erişmek için giriş yapın.')}
                    </p>

                    {formError && (
                        <div style={{ padding: '1rem', background: '#fee2e2', color: '#ef4444', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
                            <AlertCircle size={16} />
                            {formError}
                        </div>
                    )}

                    {mode === 'register' ? (
                        <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <style>{`@media (max-width: 400px) { .checkin-name-grid { grid-template-columns: 1fr !important; } }`}</style>
                            <div className="checkin-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                        <User size={16} /> {t('checkin.firstName', 'Ad')}
                                    </label>
                                    <input 
                                        required 
                                        placeholder={t('common.firstName', 'Ad')} 
                                        value={formData.firstName} 
                                        onChange={e => setFormData({ ...formData, firstName: capitalizeWords(e.target.value) })} 
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                        <User size={16} /> {t('checkin.lastName', 'Soyad')}
                                    </label>
                                    <input 
                                        required 
                                        placeholder={t('common.lastName', 'Soyad')} 
                                        value={formData.lastName} 
                                        onChange={e => setFormData({ ...formData, lastName: capitalizeWords(e.target.value) })} 
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Mail size={16} /> {t('checkin.email', 'E-posta')}
                                </label>
                                <input 
                                    type="email" 
                                    required 
                                    placeholder="ornek@email.com" 
                                    value={formData.email} 
                                    onChange={e => setFormData({ ...formData, email: e.target.value })} 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Phone size={16} /> {t('checkin.phone', 'Telefon')}
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select 
                                        value={formData.phoneCode} 
                                        onChange={e => setFormData({ ...formData, phoneCode: e.target.value })} 
                                        style={{ width: '95px', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        {countries.map(c => <option key={c.code} value={c.code}>{c.flag || ''} {c.code}</option>)}
                                    </select>
                                    <input
                                        required
                                        type="tel"
                                        inputMode="numeric"
                                        placeholder="5XX XXX XX XX"
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        maxLength={10}
                                        style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Lock size={16} /> {t('checkin.password', 'Şifre')}
                                </label>
                                <input 
                                    required 
                                    type="password"
                                    placeholder="••••••••" 
                                    value={formData.password || ''} 
                                    onChange={e => setFormData({ ...formData, password: e.target.value })} 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Lock size={16} /> {t('checkin.confirmPassword', 'Şifre Onay')}
                                </label>
                                <input 
                                    required 
                                    type="password"
                                    placeholder="••••••••" 
                                    value={formData.confirmPassword || ''} 
                                    onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>

                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                                <input
                                    type="checkbox"
                                    checked={kvkkAccepted}
                                    onChange={e => setKvkkAccepted(e.target.checked)}
                                    style={{ width: '18px', height: '18px', marginTop: '1px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary)' }}
                                />
                                <span>
                                    <a href="/kvkk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>KVKK Aydınlatma Metni</a>
                                    {' '}ve{' '}
                                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>Gizlilik Sözleşmesi</a>
                                    {"'ni okudum, kişisel verilerimin işlenmesini kabul ediyorum."}
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={status === 'loading' || !kvkkAccepted}
                                className="btn-primary"
                                style={{ height: '56px', borderRadius: '16px', fontWeight: 800, fontSize: '1.05rem', marginTop: '0.5rem', border: 'none', cursor: 'pointer' }}
                            >
                                {status === 'loading' ? t('common.loading') : t('checkin.button', 'Kayıt Ol')}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={onLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Phone size={16} /> {t('checkin.identifier', 'Telefon veya E-posta')}
                                </label>
                                <input 
                                    required 
                                    placeholder="5XX... veya e-posta" 
                                    value={loginIdentifier} 
                                    onChange={e => setLoginIdentifier(e.target.value)} 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Lock size={16} /> {t('checkin.password', 'Şifre')}
                                </label>
                                <input 
                                    required 
                                    type="password"
                                    placeholder="••••••••" 
                                    value={loginPassword} 
                                    onChange={e => setLoginPassword(e.target.value)} 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>

                            <button 
                                type="submit" 
                                disabled={status === 'loading'} 
                                className="btn-primary" 
                                style={{ height: '56px', borderRadius: '16px', fontWeight: 800, fontSize: '1.05rem', marginTop: '1rem', border: 'none', cursor: 'pointer' }}
                            >
                                {status === 'loading' ? t('common.loading') : t('checkin.loginButton', 'Giriş Yap')}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            <div style={{ padding: '2rem', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                powered by <a href="https://ugra.io" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-main)', textDecoration: 'none', fontFamily: 'var(--font-logo)', fontSize: '0.9rem', position: 'relative', top: '1px' }}>ugra.io</a>
            </div>
        </div>
    );
};

export default CheckInForm;
